// The inspector's Story section for a scene (StoryViews.swift's `InspectorStorySection`): its story time (on or off,
// when, and how long it lasts), who's in it — each linked character, place, object or motif as POV, on stage, mentioned
// or absent, "inferred" when the text or the POV field says so, with Automatic (from the text) to undo a choice — Add to
// Scene for someone present but not named, and sunrise, sunset and the moon for the date at the setting. (Setting and
// POV are the Fields above.) The web has no Scene drawer: this is its People tab.
import type { MetadataValue, Project } from '@writing-suite/core';
import {
  formatDate, moon, phaseName, PRESENCE_LABELS, PRESENCE_LEVELS, sunDay, zoneNamed, type PresenceLevel,
} from '@writing-suite/core/story';
import type { LiveProject } from '../../store/project.ts';
import { markedAbsent, opened, presenceOf, storyEdit } from '../../store/story.ts';
import { fromLocalInput, navigatorLocale, toLocalInput, useShown } from './common.tsx';

const AUTOMATIC = '__automatic';

/** Whether the inspector's fragment is one of the manuscript's scenes (the Mac shows Story only for scenes). */
export function isStoryScene(project: Project, fragmentId: string): boolean {
  const p = opened(project);
  const kind = p.record.projectType === 'screenplay' ? 'screenplay' : 'prose';
  return p.scenes([kind]).some((s) => s.id === fragmentId && !s.isFolder);
}

export function InspectorStory({ project, live, fragmentId, readOnly }: { project: Project; live: LiveProject | null; fragmentId: string; readOnly: boolean }) {
  const f = project.fragment(fragmentId);
  const storedTime = f?.metadata['story-time']?.type === 'date' ? f.metadata['story-time'].value.getTime() : null;
  const storedDuration = f?.metadata['story-duration']?.type === 'number' ? f.metadata['story-duration'].value : 1;
  const [time, showTime] = useShown<number | null>(storedTime);
  const [duration, showDuration] = useShown<number>(storedDuration);
  if (!f || !isStoryScene(project, fragmentId)) return null;
  const p = opened(project);
  const entities = p.record.entities;
  const settingId = f.metadata.setting?.type === 'text' ? f.metadata.setting.value : null;
  const place = settingId ? entities.find((e) => e.id === settingId && e.kind === 'place') : undefined;
  const rows = [...presenceOf(p, fragmentId), ...markedAbsent(p, fragmentId).map((entityID) => ({ entityID, level: 'absent' as PresenceLevel, inferred: false }))];
  const listed = new Set(rows.map((r) => r.entityID));
  const others = entities.filter((e) => !listed.has(e.id)).sort((a, b) => a.name.localeCompare(b.name));
  const set = (field: string, value: MetadataValue | null) => live?.edit((e) => e.setMetadata(fragmentId, field, value));
  const presence = (entityId: string, level: PresenceLevel | null) => live && storyEdit(live, (e) => e.setPresence(fragmentId, entityId, level));
  return (
    <section className="inspector-story" aria-labelledby="inspector-story-title" data-fragment-id={fragmentId}>
      <h3 id="inspector-story-title" className="eyebrow">
        Story
      </h3>
      <div className="field-row">
        <label htmlFor="inspector-story-time">Story time</label>
        <input
          id="inspector-story-time"
          type="checkbox"
          className="field-switch"
          checked={time !== null}
          disabled={readOnly}
          onChange={(e) => {
            const on = e.target.checked ? (time ?? Date.now()) : null;
            showTime(on);
            set('story-time', on === null ? null : { type: 'date', value: new Date(on) });
          }}
        />
      </div>
      {time !== null ? (
        <>
          <div className="field-row">
            <label htmlFor="inspector-story-when">When</label>
            <input
              id="inspector-story-when"
              type="datetime-local"
              value={toLocalInput(time)}
              disabled={readOnly}
              onChange={(e) => {
                const v = fromLocalInput(e.target.value);
                if (v === null) return;
                showTime(v);
                set('story-time', { type: 'date', value: new Date(v) });
              }}
            />
          </div>
          <div className="field-row">
            <label htmlFor="inspector-story-lasts">Lasts (hours)</label>
            <input
              id="inspector-story-lasts"
              type="number"
              min={0.25}
              max={240}
              step={0.25}
              value={duration}
              disabled={readOnly}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n) || n < 0.25 || n > 240) return;
                showDuration(n);
                set('story-duration', { type: 'number', value: n });
              }}
            />
          </div>
        </>
      ) : null}
      {time !== null && place && place.latitude !== undefined && place.longitude !== undefined ? (
        <Almanac time={time} name={place.name} latitude={place.latitude} longitude={place.longitude} zoneID={place.timeZoneID} />
      ) : null}
      <ul className="presence-rows" aria-label="Who’s in this scene">
        {rows.map((r) => {
          const e = entities.find((x) => x.id === r.entityID);
          return e ? <PresenceRow key={e.id} entityId={e.id} name={e.name} level={r.level} inferred={r.inferred} readOnly={readOnly} onChange={(level) => presence(e.id, level)} /> : null;
        })}
      </ul>
      {others.length && !readOnly ? (
        <label className="field-row">
          <span>Add to Scene</span>
          <select
            aria-label="Add to Scene"
            value=""
            onChange={(ev) => {
              if (ev.target.value) presence(ev.target.value, 'on-stage');
            }}
          >
            <option value="">Choose…</option>
            {others.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </section>
  );
}

/** Sunrise, sunset and the moon for the scene's date and place. */
function Almanac({ time, name, latitude, longitude, zoneID }: { time: number; name: string; latitude: number; longitude: number; zoneID?: string }) {
  const named = zoneNamed(zoneID);
  const zone = named ?? { secondsFromGMT: Math.trunc((longitude / 15) * 3600) };
  const day = sunDay(time, latitude, longitude, named);
  const m = moon(time);
  const clock = (ms: number | null) => (ms === null ? '—' : formatDate(ms, zone, 'omitted', 'shortened', navigatorLocale()));
  const phase = phaseName(m);
  return (
    <div className="almanac" aria-label="Sun and moon">
      <p>
        Sunrise {clock(day.sunrise)}, sunset {clock(day.sunset)}
      </p>
      <p>
        {phase[0].toUpperCase() + phase.slice(1)}, {Math.round(m.illumination * 100)}% lit
      </p>
      <p className="field-note">
        At {name}, {named ? zoneID : 'local mean time'}
      </p>
    </div>
  );
}

/** One entity's presence in the scene, shown at once when it's changed. */
function PresenceRow({ entityId, name, level: stored, inferred, readOnly, onChange }: {
  entityId: string;
  name: string;
  level: PresenceLevel;
  inferred: boolean;
  readOnly: boolean;
  onChange: (level: PresenceLevel | null) => void;
}) {
  const [level, show] = useShown<string>(stored);
  const id = `presence-${entityId}`;
  return (
    <li className="field-row" data-entity-id={entityId} data-level={level === AUTOMATIC ? stored : level} data-inferred={inferred || undefined}>
      <label htmlFor={id}>
        {name}
        {inferred ? <span className="presence-inferred"> inferred</span> : null}
      </label>
      <select
        id={id}
        value={level}
        disabled={readOnly}
        onChange={(ev) => {
          show(ev.target.value);
          onChange(ev.target.value === AUTOMATIC ? null : (ev.target.value as PresenceLevel));
        }}
      >
        {[...PRESENCE_LEVELS].reverse().map((l) => (
          <option key={l} value={l}>
            {PRESENCE_LABELS[l]}
          </option>
        ))}
        {inferred ? null : <option value={AUTOMATIC}>Automatic (from the text)</option>}
      </select>
    </li>
  );
}
